import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { gql } from '@apollo/client';
import { Button, Icon, Input, Select, useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import {
  Header,
  Title,
  Breadcrumb,
  ContentContainer,
  TabsContainer,
  TabButton,
  FormContainer,
  SectionTitle,
  SectionDescription,
  Actions,
} from './BusinessSettings.styles';
import { useRole, type Role } from '../../../context/RoleContext';
import { useUser } from '../../../context/UserContext';
import { showToast } from '../../../utils/toastUtils';

const AdminSubTabs = ['Users', 'Roles', 'Matrix', 'Audit Log', 'Delegations'] as const;
const UserSubTabs = ['My Access'] as const;
type SubTab = (typeof AdminSubTabs)[number] | (typeof UserSubTabs)[number];
type ScreenAccess = 'full' | 'view-only' | 'restricted';
type Scope = 'Global' | 'Assigned' | 'Customer-owned';
type AuthorityLevel = 'Full' | 'High' | 'Medium' | 'Low';
type MatrixCode = 'R/W' | 'R' | 'Pay' | '—';

const scopeDescriptions: Record<Scope, string> = {
  'Global': 'All shipments and data across the org',
  'Assigned': 'Shipments explicitly assigned to the user or team',
  'Customer-owned': 'Only the customer’s own shipments and payments',
};

type RoleCapability = {
  icon: string;
  text: string;
};

type RolePermission = {
  screen: string;
  access: ScreenAccess;
  matrixCode: MatrixCode;
  restrictionReason?: string | null;
  capabilities: RoleCapability[];
};

type RoleConfig = {
  role: Role;
  label: string;
  primaryScreens: string[];
  authorityLevel: AuthorityLevel;
  intent: string;
  scope: Scope;
  scopeLocked?: boolean;
  userCount: number;
  permissions: RolePermission[];
};

const getAuthorityBadgeType = (authorityLevel: AuthorityLevel): 'full' | 'view-only' | 'restricted' => {
  if (authorityLevel === 'Full') return 'full';
  if (authorityLevel === 'High') return 'view-only';
  return 'restricted';
};

const emptyRoleConfig = (role: Role): RoleConfig => ({
  role,
  label: role,
  primaryScreens: [],
  authorityLevel: 'Low',
  intent: '',
  scope: 'Assigned',
  scopeLocked: false,
  userCount: 0,
  permissions: [],
});

const scopeOptions: Array<{ label: string; value: Scope }> = [
  { label: 'Global – all shipments in org', value: 'Global' },
  { label: 'Assigned – shipments explicitly assigned', value: 'Assigned' },
  { label: 'Customer-owned – own shipments only', value: 'Customer-owned' },
];

// Audit log with business impact and categorization
const auditLogData: Array<{ time: string; user: string; role: string; action: string; object: string; impact: string; type: 'Customs' | 'Booking' | 'Payments' | 'control' }> = [
  { time: '15:40', user: 'Priya', role: 'Compliance', action: 'Submitted amendment', object: 'QRY-ICE-88231', impact: '🟢 Booking unblocked', type: 'Customs' },
  { time: '14:10', user: 'John', role: 'Ops', action: 'Confirmed booking', object: 'SCH-2026-0122', impact: '🟢 Shipment locked', type: 'Booking' },
  { time: '12:02', user: 'Admin', role: 'Admin', action: 'Suspended user', object: 'finance@abc.com', impact: '🔴 Payment approvals paused', type: 'control' },
  { time: '11:25', user: 'Finance Bot', role: 'Finance', action: 'Approved invoice', object: 'INV-001', impact: '🟢 Payment recorded', type: 'Payments' },
];

type AuditType = 'All' | 'Booking' | 'Payments' | 'Customs' | 'control';

type AccessUser = {
  id: number;
  name: string;
  email: string;
  role: Role;
  status: string;
  lastActive: string;
  scope: Scope;
  allowRoleSwitch: boolean;
  restrictSalesScope: boolean;
};

type AdminAccessUserDTO = {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  lastActive?: string | null;
  scope: Scope;
  allowRoleSwitch: boolean;
  restrictSalesScope: boolean;
};

type AdminAccessRoleSummaryDTO = {
  role: string;
  label: string;
  authorityLevel: AuthorityLevel;
  scope: Scope;
  intent: string;
  userCount: number;
  primaryScreens: string[];
  scopeLocked: boolean;
  permissions: AdminAccessScreenPermissionDTO[];
};

type AdminAccessCapabilityDTO = {
  icon: string;
  text: string;
};

type AdminAccessScreenPermissionDTO = {
  screen: string;
  access: ScreenAccess;
  matrixCode: MatrixCode;
  restrictionReason?: string | null;
  capabilities: AdminAccessCapabilityDTO[];
};

type AdminAccessAuditLogDTO = {
  id: number;
  time: string;
  user: string;
  role: string;
  action: string;
  object: string;
  impact: string;
  type: 'Customs' | 'Booking' | 'Payments' | 'control';
};

type AdminAccessQueryData = {
  adminAccessSnapshot: {
    users: AdminAccessUserDTO[];
    roles: AdminAccessRoleSummaryDTO[];
    auditLogs: AdminAccessAuditLogDTO[];
  };
};

type AdminAccessQueryVariables = {
  limit: number;
  offset: number;
  auditLimit: number;
  eventType?: string | null;
};

type InviteAdminUserMutationData = {
  inviteAdminUser: {
    success: boolean;
    message: string;
  };
};

type InviteAdminUserMutationVariables = {
  input: {
    email: string;
    role: string;
    scope: string;
    allowRoleSwitch: boolean;
    restrictSalesScope: boolean;
  };
};

type UpdateAdminUserRoleMutationData = {
  updateAdminUserRole: {
    success: boolean;
    message: string;
  };
};

type UpdateAdminUserRoleMutationVariables = {
  input: {
    email: string;
    role: string;
  };
};

type SuspendAdminUserMutationData = {
  suspendAdminUser: {
    success: boolean;
    message: string;
  };
};

type SuspendAdminUserMutationVariables = {
  input: {
    email: string;
  };
};

const ADMIN_ACCESS_QUERY = gql`
  query AdminAccessData($limit: Int!, $offset: Int!, $auditLimit: Int!, $eventType: String) {
    adminAccessSnapshot(limit: $limit, offset: $offset, auditLimit: $auditLimit, eventType: $eventType) {
      users {
        id
        name
        email
        role
        status
        lastActive
        scope
        allowRoleSwitch
        restrictSalesScope
      }
      roles {
        role
        label
        authorityLevel
        scope
        intent
        userCount
        primaryScreens
        scopeLocked
        permissions {
          screen
          access
          matrixCode
          restrictionReason
          capabilities {
            icon
            text
          }
        }
      }
      auditLogs {
        id
        time
        user
        role
        action
        object
        impact
        type
      }
    }
  }
`;

const INVITE_ADMIN_USER_MUTATION = gql`
  mutation InviteAdminUser($input: InviteAdminUserInput!) {
    inviteAdminUser(input: $input) {
      success
      message
    }
  }
`;

const UPDATE_ADMIN_USER_ROLE_MUTATION = gql`
  mutation UpdateAdminUserRole($input: UpdateAdminUserRoleInput!) {
    updateAdminUserRole(input: $input) {
      success
      message
    }
  }
`;

const SUSPEND_ADMIN_USER_MUTATION = gql`
  mutation SuspendAdminUser($input: SuspendAdminUserInput!) {
    suspendAdminUser(input: $input) {
      success
      message
    }
  }
`;

const normalizeRole = (roleValue: string): Role => {
  const value = roleValue.trim().toLowerCase();
  if (value.includes('admin')) return 'admin';
  if (value.includes('compliance')) return 'compliance';
  if (value.includes('finance')) return 'finance';
  if (value.includes('sales') || value.includes('seller')) return 'sales';
  if (value.includes('agent')) return 'agent';
  if (value.includes('customer') || value.includes('buyer')) return 'customer';
  if (value.includes('supplier') || value.includes('vendor') || value.includes('manufacturer')) return 'supplier';
  if (value === 'operations' || value === 'logistics') return 'ops';
  if (value.includes('ops') || value.includes('logistics') || value.includes('operations')) return 'ops';
  if (value === 'seller') return 'sales';
  if (value === 'vendor') return 'supplier';
  const allRoles: Role[] = ['admin', 'ops', 'compliance', 'finance', 'sales', 'agent', 'customer', 'supplier'];
  return allRoles.includes(value as Role) ? (value as Role) : 'ops';
};

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
`;
const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.gray100};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 600;
`;
const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;
const ModalCard = styled.div`
  width: 680px;
  max-height: 90vh;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;
const ModalBody = styled.div`
  padding: 20px;
  display: grid;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
`;
const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const PreviewContainer = styled.div`
  background: ${({ theme }) => theme.colors.gray100};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
`;

const PreviewSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PreviewItem = styled.div<{ $access: ScreenAccess }>`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  background: ${({ $access, theme }) => {
    if ($access === 'full') return `${theme.colors.successLight}30`;
    if ($access === 'view-only') return `${theme.colors.infoLight}30`;
    return `${theme.colors.errorLight}30`;
  }};
  color: ${({ $access, theme }) => {
    if ($access === 'full') return theme.colors.successDark;
    if ($access === 'view-only') return theme.colors.infoDark;
    return theme.colors.errorDark;
  }};
  text-decoration: ${({ $access }) => $access === 'restricted' ? 'line-through' : 'none'};
  opacity: ${({ $access }) => $access === 'restricted' ? 0.6 : 1};
`;

const Badge = styled.span<{ $type: 'full' | 'view-only' | 'restricted' }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $type }) => {
    if ($type === 'full') return '#ecfdf3';
    if ($type === 'view-only') return '#f0f9ff';
    return '#fef2f2';
  }};
  color: ${({ $type }) => {
    if ($type === 'full') return '#065f46';
    if ($type === 'view-only') return '#075985';
    return '#7f1d1d';
  }};
`;

const ConfirmDialog = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
`;

const ConfirmCard = styled.div`
  width: 420px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  overflow: hidden;
`;

const ConfirmHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ConfirmTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ConfirmBody = styled.div`
  padding: 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  line-height: 1.5;
`;

const ConfirmFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
`;

const AccessBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AccessBadge = styled.span<{ $access: 'full' | 'view-only' | 'restricted' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $access }) => {
    if ($access === 'full') return '#ecfdf3';
    if ($access === 'view-only') return '#f0f9ff';
    return '#fef2f2';
  }};
  color: ${({ $access }) => {
    if ($access === 'full') return '#065f46';
    if ($access === 'view-only') return '#075985';
    return '#7f1d1d';
  }};
`;

const RoleIntentText = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  font-weight: normal;
`;

const ScopeTag = styled.span<{ $scope: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #f3f4f6;
  color: #6b7280;
  margin-left: 8px;
`;

const RestrictedTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #7f1d1d;
  background: #fef2f2;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  margin-top: 8px;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => $active ? '#3b82f6' : '#d1d5db'};
  background: ${({ $active }) => $active ? '#dbeafe' : 'white'};
  color: ${({ $active }) => $active ? '#1e40af' : '#6b7280'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: #3b82f6;
  }
`;

const MatrixContainer = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-top: 12px;
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  th, td {
    padding: 12px 16px;
    border-right: 1px solid #e5e7eb;
    text-align: left;
  }
  
  th {
    background: #f8fafc;
    font-weight: 600;
    color: #64748b;
    border-bottom: 1px solid #e5e7eb;
  }
  
  td {
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
  }
  
  tbody tr:hover {
    background: #fafbfc;
  }
`;

const AccessCell = styled.td<{ $access: 'R/W' | 'R' | 'Pay' | '—' }>`
  font-weight: 500;
  color: ${({ $access }) => {
    if ($access === 'R/W') return '#065f46';
    if ($access === 'R') return '#075985';
    if ($access === 'Pay') return '#7c2d12';
    return '#9ca3af';
  }};
`;

export const AccessControl: React.FC = () => {
  const { role } = useRole();
  const { user } = useUser();
  const isAdmin = role === 'admin';
  const visibleSubTabs = isAdmin ? AdminSubTabs : UserSubTabs;
  const [subTab, setSubTab] = useState<SubTab>(isAdmin ? 'Users' : 'My Access');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('ops');
  const [inviteScope, setInviteScope] = useState<Scope>('Assigned');
  const [allowRoleSwitch, setAllowRoleSwitch] = useState(true);
  const [restrictSalesScope, setRestrictSalesScope] = useState(false);
  const [selectedRolePreview, setSelectedRolePreview] = useState<Role | null>(null);
  const [auditFilterType, setAuditFilterType] = useState<AuditType>('All');
  
  // Edit user modal
  const [editUserEmail, setEditUserEmail] = useState<string | null>(null);
  const [editUserRole, setEditUserRole] = useState<Role>('ops');
  
  // Suspend user confirmation
  const [suspendUserEmail, setSuspendUserEmail] = useState<string | null>(null);

  const [users, setUsers] = useState<AccessUser[]>([]);

  const { data, loading, refetch } = useGraphQLQuery<AdminAccessQueryData, AdminAccessQueryVariables>(
    ADMIN_ACCESS_QUERY,
    {
      variables: {
        limit: 500,
        offset: 0,
        auditLimit: 200,
        eventType: auditFilterType === 'All' ? null : auditFilterType,
      },
      showErrorToast: false,
    }
  );

  const [inviteAdminUser] = useGraphQLMutation<InviteAdminUserMutationData, InviteAdminUserMutationVariables>(
    INVITE_ADMIN_USER_MUTATION,
    { showErrorToast: false }
  );
  const [updateAdminUserRole] = useGraphQLMutation<UpdateAdminUserRoleMutationData, UpdateAdminUserRoleMutationVariables>(
    UPDATE_ADMIN_USER_ROLE_MUTATION,
    { showErrorToast: false }
  );
  const [suspendAdminUser] = useGraphQLMutation<SuspendAdminUserMutationData, SuspendAdminUserMutationVariables>(
    SUSPEND_ADMIN_USER_MUTATION,
    { showErrorToast: false }
  );

  const apiAuditLog = data?.adminAccessSnapshot?.auditLogs && data.adminAccessSnapshot.auditLogs.length > 0
    ? data.adminAccessSnapshot.auditLogs
    : auditLogData;

  const roleConfigs = useMemo<RoleConfig[]>(() => {
    return (data?.adminAccessSnapshot?.roles || []).map((roleSummary) => ({
      role: normalizeRole(roleSummary.role),
      label: roleSummary.label,
      primaryScreens: roleSummary.primaryScreens,
      authorityLevel: roleSummary.authorityLevel,
      intent: roleSummary.intent,
      scope: roleSummary.scope,
      scopeLocked: roleSummary.scopeLocked,
      userCount: roleSummary.userCount,
      permissions: (roleSummary.permissions || []).map((permission) => ({
        screen: permission.screen,
        access: permission.access,
        matrixCode: permission.matrixCode,
        restrictionReason: permission.restrictionReason,
        capabilities: permission.capabilities || [],
      })),
    }));
  }, [data]);

  const roleConfigMap = useMemo<Record<Role, RoleConfig>>(() => {
    return roleConfigs.reduce((accumulator, roleConfig) => {
      accumulator[roleConfig.role] = roleConfig;
      return accumulator;
    }, {} as Record<Role, RoleConfig>);
  }, [roleConfigs]);

  const permissionMap = useMemo<Record<Role, Record<string, RolePermission>>>(() => {
    return roleConfigs.reduce((accumulator, roleConfig) => {
      accumulator[roleConfig.role] = roleConfig.permissions.reduce((permissionAccumulator, permission) => {
        permissionAccumulator[permission.screen] = permission;
        return permissionAccumulator;
      }, {} as Record<string, RolePermission>);
      return accumulator;
    }, {} as Record<Role, Record<string, RolePermission>>);
  }, [roleConfigs]);

  const matrixScreens = useMemo<string[]>(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    roleConfigs.forEach((roleConfig) => {
      roleConfig.permissions.forEach((permission) => {
        if (!seen.has(permission.screen)) {
          seen.add(permission.screen);
          ordered.push(permission.screen);
        }
      });
    });
    return ordered;
  }, [roleConfigs]);

  const capabilitySections = useMemo(() => {
    return matrixScreens.map((screen) => ({
      screen,
      roles: roleConfigs
        .map((roleConfig) => ({
          role: roleConfig.role,
          label: roleConfig.label,
          permission: permissionMap[roleConfig.role]?.[screen],
        }))
        .filter((entry) => entry.permission),
    }));
  }, [matrixScreens, permissionMap, roleConfigs]);

  const inviteRoleConfig = roleConfigMap[inviteRole] || emptyRoleConfig(inviteRole);
  const selectedRoleConfig = selectedRolePreview ? (roleConfigMap[selectedRolePreview] || emptyRoleConfig(selectedRolePreview)) : null;
  const editRoleConfig = roleConfigMap[editUserRole] || emptyRoleConfig(editUserRole);
  const currentUserEmail = user?.email?.trim().toLowerCase() || '';
  const currentUserRole = user?.role ? normalizeRole(user.role) : role;
  const currentUserRecord = useMemo(() => {
    if (!currentUserEmail) {
      return null;
    }

    return users.find((entry) => entry.email.trim().toLowerCase() === currentUserEmail) || null;
  }, [currentUserEmail, users]);
  const currentUserRoleConfig = roleConfigMap[currentUserRole] || emptyRoleConfig(currentUserRole);
  const visibleUsers = isAdmin ? users : currentUserRecord ? [currentUserRecord] : [];

  const filteredAuditLog = auditFilterType === 'All'
    ? apiAuditLog
    : apiAuditLog.filter(entry => entry.type === auditFilterType);

  useEffect(() => {
    const defaultTab = isAdmin ? 'Users' : 'My Access';
    if (!visibleSubTabs.includes(subTab as never)) {
      setSubTab(defaultTab);
    }
  }, [isAdmin, subTab, visibleSubTabs]);

  useEffect(() => {
    setInviteScope(inviteRoleConfig.scope);
  }, [inviteRoleConfig.scope]);

  useEffect(() => {
    if (!roleConfigs.length) {
      return;
    }

    const nextInviteRole = roleConfigMap[inviteRole] ? inviteRole : roleConfigs[0].role;
    if (nextInviteRole !== inviteRole) {
      setInviteRole(nextInviteRole);
    }

    if (selectedRolePreview && !roleConfigMap[selectedRolePreview]) {
      setSelectedRolePreview(roleConfigs[0].role);
    }
  }, [inviteRole, roleConfigMap, roleConfigs, selectedRolePreview]);

  useEffect(() => {
    if (!data?.adminAccessSnapshot?.users) {
      return;
    }

    setUsers(
      data.adminAccessSnapshot.users.map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        role: normalizeRole(user.role),
        status: user.status,
        lastActive: user.lastActive || '—',
        scope: user.scope,
        allowRoleSwitch: user.allowRoleSwitch,
        restrictSalesScope: user.restrictSalesScope,
      }))
    );
  }, [data]);

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      showToast('Invite email is required.', 'error');
      return;
    }

    const response = await inviteAdminUser({
      variables: {
        input: {
          email: inviteEmail.trim(),
          role: inviteRole,
          scope: inviteScope,
          allowRoleSwitch,
          restrictSalesScope,
        },
      },
    });

    const result = response.data?.inviteAdminUser;
    if (!result?.success) {
      showToast(result?.message || 'Failed to send invite.', 'error');
      return;
    }

    await refetch({
      limit: 500,
      offset: 0,
      auditLimit: 200,
      eventType: auditFilterType === 'All' ? null : auditFilterType,
    });

    showToast(`Invite sent to ${inviteEmail}! (${inviteRoleConfig.label} · ${inviteScope} scope)`, 'success');
    setInviteOpen(false);
    setInviteEmail('');
  };

  const handleEditUser = (email: string) => {
    const user = users.find(u => u.email === email);
    if (user) {
      setEditUserEmail(email);
      setEditUserRole(user.role);
    }
  };

  const handleSaveUserRole = async () => {
    if (!editUserEmail) {
      return;
    }

    const response = await updateAdminUserRole({
      variables: {
        input: {
          email: editUserEmail,
          role: editUserRole,
        },
      },
    });

    const result = response.data?.updateAdminUserRole;
    if (!result?.success) {
      showToast(result?.message || 'Unable to update role.', 'error');
      return;
    }

    await refetch({
      limit: 500,
      offset: 0,
      auditLimit: 200,
      eventType: auditFilterType === 'All' ? null : auditFilterType,
    });

    const user = users.find(u => u.email === editUserEmail);
    showToast(`${user?.name}'s role updated to ${editRoleConfig.label}!`, 'success');
    setEditUserEmail(null);
  };

  const handleSuspendUser = (email: string) => {
    setSuspendUserEmail(email);
  };

  const handleConfirmSuspend = async () => {
    if (!suspendUserEmail) {
      return;
    }

    const response = await suspendAdminUser({
      variables: {
        input: {
          email: suspendUserEmail,
        },
      },
    });

    const result = response.data?.suspendAdminUser;
    if (!result?.success) {
      showToast(result?.message || 'Unable to suspend user.', 'error');
      return;
    }

    await refetch({
      limit: 500,
      offset: 0,
      auditLimit: 200,
      eventType: auditFilterType === 'All' ? null : auditFilterType,
    });

    const user = users.find(u => u.email === suspendUserEmail);
    showToast(`${user?.name} has been suspended.`, 'success');
    setSuspendUserEmail(null);
  };

  return (
    <>
      <Header>
        <Title>Control</Title>
        <Breadcrumb>
          <Icon name="LayoutDashboard" size={16} />
          <span>/</span>
          <span>Settings</span>
          <span>/</span>
          <span>Control</span>
        </Breadcrumb>
      </Header>

      {isAdmin ? (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', color: '#7c2d12', fontSize: '13px' }}>
          Admin actions may override operational safeguards. Proceed with care and document changes.
        </div>
      ) : (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: '13px' }}>
          This page shows only your account details and access scope.
        </div>
      )}

      <ContentContainer>
        <TabsContainer>
          {visibleSubTabs.map((t) => (
            <TabButton key={t} $active={subTab === t} onClick={() => setSubTab(t)}>
              {t}
            </TabButton>
          ))}
        </TabsContainer>

        {!isAdmin && subTab === 'My Access' && (
          <FormContainer initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle>My Access</SectionTitle>
            <SectionDescription>Your account details and the screens available to your role.</SectionDescription>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#ffffff' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Name</div>
                    <strong>{currentUserRecord?.name || user?.name || 'Current user'}</strong>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Email</div>
                    <strong>{currentUserRecord?.email || user?.email || 'Not available'}</strong>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Role</div>
                    <strong>{currentUserRoleConfig.label}</strong>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Scope</div>
                    <strong>{currentUserRecord?.scope || user?.scope || currentUserRoleConfig.scope}</strong>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                    <strong>{currentUserRecord?.status || user?.status || 'Active'}</strong>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Last Active</div>
                    <strong>{currentUserRecord?.lastActive || '—'}</strong>
                  </div>
                </div>
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#ffffff' }}>
                <SectionTitle style={{ marginBottom: '8px' }}>Accessible Screens</SectionTitle>
                <AccessBadgeContainer>
                  {currentUserRoleConfig.permissions
                    .filter((permission) => permission.access !== 'restricted')
                    .map((permission) => (
                      <AccessBadge key={permission.screen} $access={permission.access} title={permission.restrictionReason || permission.screen}>
                        {permission.screen}
                      </AccessBadge>
                    ))}
                  {currentUserRoleConfig.permissions.filter((permission) => permission.access !== 'restricted').length === 0 && (
                    <span style={{ color: '#64748b', fontSize: '13px' }}>No screen access is configured for this account.</span>
                  )}
                </AccessBadgeContainer>
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#ffffff' }}>
                <SectionTitle style={{ marginBottom: '8px' }}>Capabilities</SectionTitle>
                <div style={{ display: 'grid', gap: '8px', fontSize: '13px', color: '#334155' }}>
                  {currentUserRoleConfig.permissions
                    .filter((permission) => permission.access !== 'restricted')
                    .flatMap((permission) => permission.capabilities.map((capability) => ({
                      key: `${permission.screen}-${capability.text}`,
                      value: `${capability.icon} ${capability.text}`,
                    })))
                    .map((capability) => (
                      <div key={capability.key}>{capability.value}</div>
                    ))}
                  {currentUserRoleConfig.permissions.filter((permission) => permission.access !== 'restricted').every((permission) => permission.capabilities.length === 0) && (
                    <div>No additional capabilities configured for this role.</div>
                  )}
                </div>
              </div>
            </div>
          </FormContainer>
        )}

        {/* Users Screen */}
        {isAdmin && subTab === 'Users' && (
          <FormContainer initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle>Users</SectionTitle>
            <SectionDescription>Manage who has to Tvikla and what they can do.</SectionDescription>
            <Actions>
              <Button onClick={() => setInviteOpen(true)}>
                <Icon name="UserPlus" size={16} /> Invite User
              </Button>
            </Actions>
            <div style={{ overflowX: 'auto', borderRadius: '8px' }}>
              <Table>
                <thead>
                  <tr>
                    <Th>User</Th><Th>Email</Th><Th>Role & Scope</Th><Th>Access</Th><Th>Status</Th><Th style={{ minWidth: '150px' }}>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && users.length === 0 && (
                    <tr>
                      <Td colSpan={6}>Loading users...</Td>
                    </tr>
                  )}
                  {visibleUsers.map((u) => {
                    const screensList = (roleConfigMap[u.role]?.permissions || [])
                      .filter((permission) => permission.access !== 'restricted')
                      .map((permission) => permission.screen);
                    const visibleScreens = screensList.slice(0, 3);
                    const hiddenCount = Math.max(0, screensList.length - 3);
                    const userRoleConfig = roleConfigMap[u.role] || emptyRoleConfig(u.role);
                    
                    return (
                      <tr key={`${u.id}-${u.email}`}>
                        <Td><strong>{u.name}</strong></Td>
                        <Td>{u.email}</Td>
                        <Td>
                          <div>{userRoleConfig.label}</div>
                          {u.scope && (
                            <ScopeTag $scope={u.scope}>
                              {u.scope === 'Global' && '🌍'}
                              {u.scope === 'Assigned' && '📦'}
                              {u.scope === 'Customer-owned' && '👤'}
                              {u.scope}
                            </ScopeTag>
                          )}
                        </Td>
                        <Td>
                          <AccessBadgeContainer>
                            {visibleScreens.map((screen) => {
                              const permission = permissionMap[u.role]?.[screen];
                              if (!permission) {
                                return null;
                              }
                              const title = permission.access === 'restricted'
                                ? `Restricted by role: ${userRoleConfig.label} cannot access ${screen}`
                                : permission.access === 'view-only'
                                  ? (permission.restrictionReason ? `View-only: ${permission.restrictionReason}` : `View-only: ${userRoleConfig.label} can view ${screen}`)
                                  : `Full access to ${screen}`;
                              return (
                                <AccessBadge
                                  key={screen}
                                  $access={permission.access}
                                  title={title}
                                >
                                  {screen}
                                </AccessBadge>
                              );
                            })}
                            {hiddenCount > 0 && (
                              <AccessBadge $access="view-only" title={screensList.slice(3).join(', ')}>
                                +{hiddenCount} more
                              </AccessBadge>
                            )}
                          </AccessBadgeContainer>
                        </Td>
                        <Td>{u.status}</Td>
                        <Td>
                          <RowActions style={{ minWidth: '140px' }}>
                            <Button variant="muted" onClick={() => handleEditUser(u.email)}><Icon name="Pencil" size={14} /> Edit</Button>
                            <Button variant="muted" onClick={() => handleSuspendUser(u.email)}><Icon name="AlertCircle" size={14} /> Suspend</Button>
                          </RowActions>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {inviteOpen && (
              <ModalOverlay onClick={() => setInviteOpen(false)}>
                <ModalCard onClick={(e) => e.stopPropagation()}>
                    <ModalHeader>
                    <strong>Invite User to Tvikla</strong>
                    <Button variant="ghost" size="icon" onClick={() => setInviteOpen(false)} aria-label="Close invite modal"><Icon name="X" size={16} /></Button>
                  </ModalHeader>
                  <ModalBody>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      {/* Left: Invite Form */}
                      <div>
                        <Input 
                          type="email"
                          validateEmail
                          label="Email Address" 
                          placeholder="user@company.com" 
                          value={inviteEmail} 
                          onChange={(e: any) => setInviteEmail(e.target.value)} 
                          helperText="We’ll use this to send the invite"
                        />
                        <Select
                          label="Assign Role"
                          value={inviteRole}
                          onChange={(e) => setInviteRole((e.target as HTMLSelectElement).value as Role)}
                          items={roleConfigs.map((roleConfig) => ({ label: `${roleConfig.label} - ${roleConfig.intent}`, value: roleConfig.role }))}
                        />
                        <Select
                          label="Scope"
                          value={inviteScope}
                          onChange={(e) => setInviteScope((e.target as HTMLSelectElement).value as Scope)}
                          disabled={inviteRoleConfig.scopeLocked}
                          items={scopeOptions.map(o => ({ label: o.label, value: o.value }))}
                          helperText={inviteRoleConfig.scopeLocked ? `${inviteRoleConfig.label} scope is fixed for safety` : scopeDescriptions[inviteScope]}
                        />
                        <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '13px' }}>
                            <input type="checkbox" checked={allowRoleSwitch} onChange={(e) => setAllowRoleSwitch(e.target.checked)} /> 
                            Allow role switching (dev testing)
                          </label>
                          {inviteRole === 'sales' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '13px' }}>
                              <input type="checkbox" checked={restrictSalesScope} onChange={(e) => setRestrictSalesScope(e.target.checked)} /> 
                              Restrict to specific customers
                            </label>
                          )}
                        </div>
                      </div>

                      {/* Right: Live Preview */}
                      <div style={{ borderLeft: '1px solid #e5e7eb', paddingLeft: '16px' }}>
                        <strong style={{ display: 'block', marginBottom: '12px', fontSize: '13px', color: '#64748b' }}>WHAT THEY'LL HAVE TO</strong>
                        <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '12px', color: '#64748b' }}>
                          <strong>{inviteRoleConfig.label}</strong><br/>
                          {inviteRoleConfig.intent}<br/>
                          <span style={{ color: '#0f172a' }}>This role operates at: {inviteScope} scope</span><br/>
                          <span>{scopeDescriptions[inviteScope]}</span>
                        </div>
                        <div style={{ fontSize: '12px', display: 'grid', gap: '4px' }}>
                          {inviteRoleConfig.permissions.map((permission) => (
                            <div key={permission.screen} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0', color: permission.access === 'restricted' ? '#999' : '#0f172a' }}>
                              {permission.access === 'full' && '🟢'} 
                              {permission.access === 'view-only' && '⚪'} 
                              {permission.access === 'restricted' && '🔒'}
                              <span style={{ textDecoration: permission.access === 'restricted' ? 'line-through' : 'none' }}>{permission.screen}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Capabilities Details */}
                    <div style={{ gridColumn: '1 / -1', background: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#075985' }}>
                      <strong>Key Capabilities:</strong>
                          <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                            {inviteRoleConfig.permissions.flatMap((permission) => permission.capabilities).slice(0, 6).map((capability) => (
                          <li key={`${capability.icon}-${capability.text}`}>{capability.icon} {capability.text}</li>
                        ))}
                        {inviteRoleConfig.permissions.flatMap((permission) => permission.capabilities).length === 0 && <li>View-only to most screens</li>}
                      </ul>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="muted" onClick={() => setInviteOpen(false)}>Cancel</Button>
                    <Button onClick={handleSendInvite}>Send Invite</Button>
                  </ModalFooter>
                </ModalCard>
              </ModalOverlay>
            )}
          </FormContainer>
        )}

        {/* Roles Screen */}
        {isAdmin && subTab === 'Roles' && (
          <FormContainer initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle>Roles Overview</SectionTitle>
            <SectionDescription>System roles and their levels. Roles are fixed in v1.</SectionDescription>
            
            <SectionTitle style={{ marginTop: '24px' }}>Role Map</SectionTitle>
            <Table>
              <thead>
                <tr>
                  <Th>Role</Th><Th>Primary Screens</Th><Th>Authority Level</Th><Th>Scope</Th><Th>Users</Th>
                </tr>
              </thead>
              <tbody>
                {roleConfigs.map((roleConfig) => (
                  <tr key={roleConfig.role} onClick={() => setSelectedRolePreview(selectedRolePreview === roleConfig.role ? null : roleConfig.role)} style={{ cursor: 'pointer' }}>
                    <Td>
                      <div><strong>{roleConfig.label}</strong></div>
                      <RoleIntentText>{roleConfig.intent}</RoleIntentText>
                    </Td>
                    <Td>{roleConfig.primaryScreens.join(', ')}</Td>
                    <Td><Badge $type={getAuthorityBadgeType(roleConfig.authorityLevel)}>{roleConfig.authorityLevel}</Badge></Td>
                    <Td>
                      <ScopeTag $scope={roleConfig.scope} title={scopeDescriptions[roleConfig.scope]}>
                        {roleConfig.scope === 'Global' && '🌍'}
                        {roleConfig.scope === 'Assigned' && '📦'}
                        {roleConfig.scope === 'Customer-owned' && '👤'}
                        {roleConfig.scope}
                      </ScopeTag>
                    </Td>
                    <Td>{roleConfig.userCount}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {selectedRoleConfig && (
              <PreviewContainer>
                <strong style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Role Preview: {selectedRoleConfig.label}</span>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedRolePreview(null)} aria-label="Close role preview">
                    <Icon name="X" size={16} />
                  </Button>
                </strong>
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#0f172a' }}>
                  This role operates at: <strong>{selectedRoleConfig.scope}</strong> scope — {scopeDescriptions[selectedRoleConfig.scope]}
                </div>
                <div style={{ marginTop: '12px', padding: '12px 0' }}>
                  <strong style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '8px' }}>SIDEBAR VIEW:</strong>
                  <PreviewSidebar>
                    {selectedRoleConfig.permissions.map((permission) => (
                      <PreviewItem key={permission.screen} $access={permission.access}>
                        {permission.access === 'full' && '🟢'} {permission.access === 'view-only' && '⚪'} {permission.access === 'restricted' && '🔒'} {permission.screen}
                      </PreviewItem>
                    ))}
                  </PreviewSidebar>
                </div>
              </PreviewContainer>
            )}

            <SectionTitle style={{ marginTop: '24px' }}>Functional Capabilities</SectionTitle>
            <div style={{ display: 'grid', gap: '16px' }}>
              {capabilitySections.map(({ screen, roles }) => (
                <div key={screen} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                  <strong>{screen}</strong>
                  <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {roles.map(({ role: capabilityRole, label, permission }) => {
                      if (!permission) {
                        return null;
                      }
                      const badge = permission.access;
                      const whyRestricted = permission.restrictionReason;
                      return (
                        <div key={capabilityRole} style={{ padding: '10px', background: '#f9fafb', borderRadius: '6px', fontSize: '13px' }}>
                          <div style={{ fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {badge === 'full' && '🟢'} {badge === 'view-only' && '⚪'} {badge === 'restricted' && '🔒'}
                            {label}
                          </div>
                          <div style={{ color: '#64748b', fontSize: '12px' }}>
                            {permission.capabilities.map((capability) => (
                              <div key={capability.text} style={{ marginBottom: '4px' }}>
                                {capability.icon} {capability.text}
                              </div>
                            ))}
                            {permission.capabilities.length === 0 && <div>No additional capabilities configured.</div>}
                          </div>
                          {badge === 'view-only' && whyRestricted && (
                            <RestrictedTooltip>
                              ℹ️ {whyRestricted}
                            </RestrictedTooltip>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: '#fef3c7', border: '1px solid #fbbf24', color: '#92400e' }}>
              <strong>DB-backed role model</strong>
              <p style={{ margin: '8px 0 0', fontSize: '13px' }}>These role definitions now come from the database. Updating the role config records will change this page without redeploying the frontend.</p>
            </div>
          </FormContainer>
        )}

        {/* Matrix Tab */}
        {isAdmin && subTab === 'Matrix' && (
          <FormContainer initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle>Role → Screen Assignment Matrix</SectionTitle>
            <SectionDescription>Single source of truth for role-to-screen levels. Read-only reference for support and compliance.</SectionDescription>
            <div style={{ display: 'grid', gap: 4, marginBottom: 12, color: '#475569', fontSize: '13px' }}>
              <span>• View-only means: can view but cannot change shipment data.</span>
              <span>• Customs actions: Compliance can respond to queries and submit amendments; others cannot.</span>
              <span>• Payments actions: Only Finance and Customers can trigger financial commitments.</span>
            </div>
            <MatrixContainer>
              <MatrixTable>
                <thead>
                  <tr>
                    <th style={{ minWidth: '150px' }}>Screen</th>
                    <th>Admin</th>
                    <th>Ops</th>
                    <th>Compliance</th>
                    <th>Finance</th>
                    <th>Sales</th>
                    <th>Agent</th>
                    <th>Customer</th>
                    <th>Supplier</th>
                  </tr>
                </thead>
                <tbody>
                  {matrixScreens.map(screen => (
                    <tr key={screen}>
                      <td><strong>{screen}</strong></td>
                      {roleConfigs.map((roleConfig) => {
                        const permission = permissionMap[roleConfig.role]?.[screen];
                        const access = permission?.access || 'restricted';
                        const displayAccess = permission?.matrixCode || '—';
                        const restrictionReason = permission?.restrictionReason;
                        const tooltip = restrictionReason
                          ? `Restricted by role: ${restrictionReason}`
                          : access === 'view-only'
                            ? 'Can view but cannot change data on this screen'
                            : access === 'restricted'
                              ? `${roleConfig.label} cannot access ${screen}`
                              : access === 'full'
                                ? 'Read and write access'
                                : 'No access';
                        return (
                          <AccessCell key={`${screen}-${roleConfig.role}`} $access={displayAccess} title={tooltip}>
                            {displayAccess}
                          </AccessCell>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </MatrixTable>
            </MatrixContainer>
            
            <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <div style={{ padding: 12, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                <strong style={{ color: '#075985', fontSize: 12 }}>R/W = Read & Write</strong>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#0c4a6e' }}>Full read and write to screen and features</p>
              </div>
              <div style={{ padding: 12, background: '#f3f4f6', borderRadius: 8, border: '1px solid #d1d5db' }}>
                <strong style={{ color: '#6b7280', fontSize: 12 }}>R = Read-only</strong>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#374151' }}>View without edit permissions</p>
              </div>
              <div style={{ padding: 12, background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
                <strong style={{ color: '#7f1d1d', fontSize: 12 }}>Pay = Limited Write</strong>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#7c2d12' }}>Specific actions only (e.g., customer payments)</p>
              </div>
            </div>
          </FormContainer>
        )}

        {/* Audit Log */}
        {isAdmin && subTab === 'Audit Log' && (
          <FormContainer initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle>Audit Log</SectionTitle>
            <SectionDescription>Accountability and compliance trail with business impact.</SectionDescription>
            
            <FilterContainer>
              {['All', 'Booking', 'Payments', 'Customs', 'control'].map(filterType => (
                <FilterButton 
                  key={filterType}
                  $active={auditFilterType === filterType}
                  onClick={() => setAuditFilterType(filterType as any)}
                >
                  {filterType}
                </FilterButton>
              ))}
            </FilterContainer>

            <Table>
              <thead>
                <tr>
                  <Th>Time</Th><Th>User</Th><Th>Role</Th><Th>Action</Th><Th>Object</Th><Th>Impact</Th>
                </tr>
              </thead>
              <tbody>
                {filteredAuditLog.map((entry, idx) => (
                  <tr key={idx}>
                    <Td>{entry.time}</Td>
                    <Td>{entry.user}</Td>
                    <Td>{entry.role}</Td>
                    <Td>{entry.action}</Td>
                    <Td><strong>{entry.object}</strong></Td>
                    <Td style={{ fontSize: '13px', fontWeight: 500 }}>{entry.impact}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div style={{ marginTop: 16, padding: 16, borderRadius: 8, background: '#f0f9ff', border: '1px solid #bfdbfe', color: '#075985', fontSize: '13px' }}>
              <strong>💡 Impact Column Explained</strong>
              <p style={{ margin: '8px 0 0' }}>Shows business-level consequences of actions. 🟢 indicates positive progress, 🔴 indicates system state changes.</p>
            </div>
          </FormContainer>
        )}

        {isAdmin && subTab === 'Delegations' && (
          <FormContainer initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <SectionTitle>Delegations (Admin-only)</SectionTitle>
            <SectionDescription>Reserved space for v2: temporary coverage with expiry and audit-safe delegation history.</SectionDescription>
            <div style={{ padding: 16, borderRadius: 8, border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: '13px' }}>
              <p style={{ margin: 0 }}><strong>Coming soon:</strong> assign short-term delegates (e.g., “Priya on leave → John covers customs for 2 days”) with automatic expiry and audit logging.</p>
              <p style={{ margin: '8px 0 0' }}>Design kept intentionally open so this tab can host delegation rules without disrupting existing tabs.</p>
            </div>
          </FormContainer>
        )}

        {/* Edit User Modal */}
        {editUserEmail && (
          <ModalOverlay onClick={() => setEditUserEmail(null)}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <Title>Edit User Role</Title>
                <Button variant="ghost" size="icon" onClick={() => setEditUserEmail(null)} aria-label="Close edit user modal">
                  <Icon name="X" size={16} />
                </Button>
              </ModalHeader>
              <ModalBody>
                <FormContainer>
                  <div>
                    <SectionTitle style={{ marginBottom: '8px' }}>Current User</SectionTitle>
                    <SectionDescription style={{ marginBottom: '16px' }}>
                      {users.find(u => u.email === editUserEmail)?.name} ({editUserEmail})
                    </SectionDescription>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#1f2937' }}>Select New Role</label>
                    <select
                      value={editUserRole}
                      onChange={(e) => setEditUserRole(e.target.value as Role)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    >
                      {roleConfigs.map((roleConfig) => (
                        <option key={roleConfig.role} value={roleConfig.role}>{roleConfig.label} - {roleConfig.intent}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ background: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#075985', marginBottom: '16px' }}>
                    <strong>New Access:</strong>
                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {editRoleConfig.permissions.map((permission) => (
                        <Badge key={permission.screen} $type={permission.access}>{permission.screen}</Badge>
                      ))}
                    </div>
                  </div>
                </FormContainer>
              </ModalBody>
              <ModalFooter>
                <Button variant="muted" onClick={() => setEditUserEmail(null)}>Cancel</Button>
                <Button onClick={handleSaveUserRole}>Save Changes</Button>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}

        {/* Suspend User Confirmation */}
        {suspendUserEmail && (
          <ConfirmDialog onClick={() => setSuspendUserEmail(null)}>
            <ConfirmCard onClick={(e) => e.stopPropagation()}>
              <ConfirmHeader>
                <ConfirmTitle>Suspend User</ConfirmTitle>
              </ConfirmHeader>
              <ConfirmBody>
                <p>Are you sure you want to suspend <strong>{users.find(u => u.email === suspendUserEmail)?.name}</strong>?</p>
                <p style={{ marginTop: '12px', fontSize: '13px', color: '#94a3b8' }}>Suspended users cannot the system. They can be reactivated later.</p>
              </ConfirmBody>
              <ConfirmFooter>
                <Button variant="muted" onClick={() => setSuspendUserEmail(null)}>Cancel</Button>
                <Button variant="destructive" onClick={handleConfirmSuspend}>Suspend User</Button>
              </ConfirmFooter>
            </ConfirmCard>
          </ConfirmDialog>
        )}
      </ContentContainer>
    </>
  );
};

export default AccessControl;
